import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { Company } from "@/types";
import { approveInvitedCompany, rejectInvitedCompany } from "@/services/inviteCompany";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { getApiErrorCode } from "@/utils/apiError";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import ApproveCompanyModal, { ApproveCompanyValues } from "../ApproveCompanyModal";

interface InvitedCompanyActionsProps {
  company?: Company;
  onClose: () => void;
}

const APPROVE_ERROR_KEYS: Record<string, string> = {
  "USR-233": "validation:invalidNorwegianOrgNumber",
  "USR-237": "validation:dunsLength",
  "USR-238": "validation:orgNumberRequired",
};

/** Approve (with org number + DUNS) / reject for a company a customer invited (open COMPANY_INVITED). */
export default function InvitedCompanyActions({ company, onClose }: InvitedCompanyActionsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [approveModalOpen, setApproveModalOpen] = useState(false);

  const { mutate: mutateApproveCompany, isPending: isPendingApproveCompany } = useMutation({
    mutationFn: async (values: ApproveCompanyValues) => {
      await approveInvitedCompany(company?.id as number, values);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
      await queryClient.invalidateQueries({ queryKey: ["getCompanyById", company?.id] });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.updateSuccess"), t("notifications:admin.companyApproved"))
      );
      setApproveModalOpen(false);
      onClose();
    },
    onError: (error) => {
      console.error("Error approving company:", error);
      // Keep the modal open so the admin can correct the org number / DUNS.
      const code = getApiErrorCode(error);
      const messageKey = (code && APPROVE_ERROR_KEYS[code]) || "notifications:admin.companyApprovedError";
      notifications.show(ErrorNotificationObj(t("notifications:generic.updateError"), t(messageKey)));
    },
  });

  const { mutate: mutateRejectCompany, isPending: isPendingRejectCompany } = useMutation({
    mutationFn: async () => {
      await rejectInvitedCompany(company?.id as number);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
      await queryClient.invalidateQueries({ queryKey: ["getCompanyById", company?.id] });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.updateSuccess"), t("notifications:admin.companyRejected"))
      );
      onClose();
    },
    onError: (error) => {
      console.error("Error rejecting company:", error);
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.updateError"), t("notifications:admin.companyRejectedError"))
      );
      onClose();
    },
  });

  const isPending = isPendingApproveCompany || isPendingRejectCompany;

  return (
    <>
      {company && (
        <ApproveCompanyModal
          company={company}
          opened={approveModalOpen}
          loading={isPendingApproveCompany}
          onClose={() => setApproveModalOpen(false)}
          onApprove={(values) => mutateApproveCompany(values)}
        />
      )}
      <Group align={"flex-start"} justify={"flex-start"} w={"100%"}>
        <CTA intent="secondary" disabled={!company?.id} onClick={() => mutateRejectCompany()} loading={isPending}>
          {t("actions:reject")}
        </CTA>
        <CTA onClick={() => setApproveModalOpen(true)} disabled={!company?.id} loading={isPending}>
          {t("actions:approve")}
        </CTA>
      </Group>
    </>
  );
}
